import type { DeviceState } from '@kw/device-service/dist/device/entities/deviceState.entity';
import type { Indicator as RiskIndicator } from '@kw/service-definitions-risk_score';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { JSXElementConstructor, useContext, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GetDeviceStatePolicyStatusResponse } from '@kw/service-definitions-rule';
import { useDebounce } from 'use-debounce';
import { useLocation } from 'react-router-dom';
import { QContainer } from '@kw/quokka-ui';
import { useApplicationSortParams } from '../Applications/useApplicationSortParams';
import { RiskLevelPane } from './RiskLevelPane';
import { useInstalledApps } from './useInstalledApps';
import { PageLoader } from '../../PageLoader';
import { getMostRecentState } from '../../utils/getMostRecentState';
import { getOldestState } from '../../utils/getOldestState';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { useFetchService } from '../../utils/fetchService';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { createQueryString } from '../../utils/createQueryString';
import { getTriggeredPoliciesListString } from '../../utils/getTriggeredPoliciesListString';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';
import { NavigationContext } from '../Navigation/NavigationProvider';
import BackButton from '../shared/BackButton';

export default function DeviceDetailsPage() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [appIcons = {}, updateAppIcons] = useLocalStorage('appIcons', {});
  const [currentDeviceStateId, setCurrentDeviceStateId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const flags: LaunchDarklyFlags['flags'] = useFlags();
  const location = useLocation();
  const { deviceId } = Object.fromEntries(new URLSearchParams(location.search));

  const { deviceDetails, setDeviceDetails } = useContext(NavigationContext);

  const {
    appNameOrder,
    toggleAppNameOrder,
    appIdOrder,
    toggleAppIdOrder,
    developerOrder,
    numberOfIorsOrder,
    toggleNumberOfIorsOrder,
    securityRiskLevelOrder,
    privacyRiskLevelOrder,
  } = useApplicationSortParams();

  const { data: device, error: deviceDataError } = useFetchService(`/device/${deviceId}`, accessToken);

  const queryParamString = createQueryString({ deviceStateId: currentDeviceStateId });
  const shouldFetch = Boolean(currentDeviceStateId);
  const deviceStatePolicyStatusPath = `/eval/policies/device-state-policy-status?${queryParamString}`;

  const { data: response, error: policyStatusError } = useFetchService<GetDeviceStatePolicyStatusResponse>(
    deviceStatePolicyStatusPath,
    accessToken,
    shouldFetch,
  );

  const triggeredPoliciesListString = getTriggeredPoliciesListString(response);

  let deviceSerial;
  if (device) {
    deviceSerial = device.serial;
  }

  useEffect(() => {
    /* 
      HACK - to get correct default sort order (Issues column DESC)
      in dev environment, using react strict mode, components re-render an extra time which executes toggle twice
      in production, components only render once so we need to execute toggle twice
     */
    toggleNumberOfIorsOrder();
    if (process.env.NODE_ENV !== 'development') {
      toggleNumberOfIorsOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canListApps: boolean = verifyAuthPermission(AuthPermissions.LIST_DEVICE_APPS);

  const { apps: topSecurityScoreApps, appDataError: topSecurityScoreAppsError } = useInstalledApps(
    canListApps,
    {
      pageSize: 10,
      page: 0,
      deviceId,
      securityRiskLevelOrder: 'DESC',
    },
    accessToken,
  );

  const { apps: topPrivacyScoreApps, appDataError: topPrivacyScoreAppsError } = useInstalledApps(
    canListApps,
    {
      pageSize: 10,
      page: 0,
      deviceId,
      privacyRiskLevelOrder: 'DESC',
    },
    accessToken,
  );

  let latestDeviceState;

  if (device) {
    latestDeviceState = getMostRecentState<DeviceState>(device.states);
  }

  useEffect(() => {
    if (latestDeviceState) {
      setCurrentDeviceStateId(latestDeviceState.id);
    }
  }, [latestDeviceState]);

  useEffect(() => {
    if (device) {
      setDeviceDetails({ deviceName: device.name, deviceId: device.id });
    }
  }, [device, setDeviceDetails]);

  if (deviceDataError || topSecurityScoreAppsError || topPrivacyScoreAppsError) {
    if (deviceDataError) console.error(deviceDataError);
    if (topSecurityScoreAppsError) console.error(topSecurityScoreAppsError);
    if (topPrivacyScoreAppsError) console.error(topPrivacyScoreAppsError);

    return <div>failed to load</div>;
  }

  if (policyStatusError) {
    console.log('Unable to get policy status');
  }

  if (!device) {
    return <PageLoader />;
  }

  const oldestDeviceState = getOldestState<DeviceState>(device.states);
  const ownershipMap = {
    company: 'Company-Owned',
    personal: 'Personal-Owned',
  };

  const deviceMetaData = [
    ['OS Version', latestDeviceState.osVersion],
    ['IMEI', device.imei],
    ['Model', device.hardware.modelName],
    ['First Seen', dayjs(oldestDeviceState.createdDate).format('MM/DD/YYYY HH:mm:ss A')],
    ['OS Build Version', latestDeviceState.osBuildVersion],
    ['Serial Number', deviceSerial],
    ['Owner', ownershipMap[device.ownership]],
    ['Last Seen', dayjs(latestDeviceState.createdDate).format('MM/DD/YYYY HH:mm:ss A')],
  ];

  const { riskIndicators } = latestDeviceState;

  const securityIorsCount = (riskIndicators as unknown as RiskIndicator[]).filter(ind => ind.isSecurityConcern).length;

  const privacyIorsCount = (riskIndicators as unknown as RiskIndicator[]).filter(ind => ind.isPrivacyConcern).length;

  const dedupeRiskLevelApps = (arr: { title: string }[]) => {
    const riskLevelApps = [...new Set(arr.map(obj => obj.title))].slice(0, 5);
    return riskLevelApps.map(
      title =>
        [title, device.os === 'android' ? <StyledAndroidIcon /> : <StyledAppleIcon />] as [
          string,
          React.ReactElement<any, string | JSXElementConstructor<any>>,
        ],
    );
  };

  return (
    <StyledQContainer>
      <BackgroundProgressBar />
      <BackButton href="/devices" text="Back to Device List" />
      <StyledContent>
        <StyledMetaDataTable aria-label="device-meta-data-table">
          <tbody>
            <StyledMetaDataRow>
              {deviceMetaData.map(([title, dataValue]) => (
                <td key={title + dataValue}>
                  <StyledMetaDataTitle>{title}</StyledMetaDataTitle>
                  <StyledMetaDataText>{dataValue}</StyledMetaDataText>
                </td>
              ))}
            </StyledMetaDataRow>
          </tbody>
        </StyledMetaDataTable>

        <RiskLevelPane
          title="Security Risk Score"
          canListApps={canListApps}
          score={latestDeviceState.securityRiskLevel}
          numberOfIors={securityIorsCount}
          topRiskyApps={canListApps && dedupeRiskLevelApps(topSecurityScoreApps)}
        />

        <RiskLevelPane
          title="Privacy Risk Score"
          canListApps={canListApps}
          score={latestDeviceState.privacyRiskLevel}
          numberOfIors={privacyIorsCount}
          topRiskyApps={canListApps && dedupeRiskLevelApps(topPrivacyScoreApps)}
          triggeredPoliciesListString={triggeredPoliciesListString}
        />
      </StyledContent>
    </StyledQContainer>
  );
}
/** styles */

const StyledAppleIcon = styled(AppleIcon)`
  font-size: 32px;
  color: #a2aaad;
`;

const StyledAndroidIcon = styled(AndroidIcon)`
  font-size: 32px;
  color: #a4c639;
`;

const StyledQContainer = styled(QContainer)`
  padding: 8px 32px;
`;

const StyledContent = styled('div')`
  display: grid;
  grid-template-areas:
    'metadata' auto
    'security' auto
    'privacy.' auto
    'table' auto
    / 100%;

  grid-row-gap: 24px;
  margin-bottom: 35px;
`;

const StyledMetaDataTable = styled('table')`
  padding: 24px;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.grey[50]};
`;

const StyledMetaDataRow = styled('tr')`
  display: grid;
  grid-template-columns: repeat(4, max(21%));
  row-gap: 24px;
`;

const StyledMetaDataTitle = styled('div')`
  font-size: 10px;
  line-height: 14px;
  color: '#818181';
`;

const StyledMetaDataText = styled('div')`
  font-size: 14px;
  line-height: 21px;
  color: #424242;
`;
